package com.vidaensupalabra.vsp.ventanas

import kotlinx.browser.window
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.coroutines.await
import com.vidaensupalabra.vsp.utils.shareText
import com.vidaensupalabra.vsp.datos.ComunidadPost

// Optimización: Compilar expresiones regulares solo una vez a nivel de archivo
internal val BOLD_REGEX = Regex("\\*\\*(.*?)\\*\\*")
internal val ITALIC_REGEX = Regex("\\*(.*?)\\*")
internal val LINK_REGEX = Regex("\\[(.*?)\\]\\((.*?)\\)")
internal val IMG_REGEX = Regex("!\\[(.*?)\\]\\((.*?)\\)")
internal val QUOTE_REGEX = Regex("^&gt; (.*?)$", RegexOption.MULTILINE)
internal val AUTO_LINK_REGEX = Regex("(^|[\\s\n]|&gt;)(https?:\\/\\/[\\w\\-.~:/?#\\[\\]@!$&'()*+,;=%]*[\\w\\-~/?#\\[\\]@$&*+=%])")
internal val YOUTUBE_REGEX = Regex("(?:youtube\\.com\\/(?:[^\\/]+\\/.+\\/|(?:v|e(?:mbed)?)\\/|.*[?&]v=)|youtu\\.be\\/)([^\"&?\\/\\s]{11})")
internal val NATIVE_VIDEO_REGEX = Regex(".*\\.(mp4|webm|ogg)(\\?.*)?$", RegexOption.IGNORE_CASE)
internal val INSTAGRAM_REGEX = Regex("https?:\\/\\/(www\\.)?instagram\\.com\\/(p|reel|tv)\\/([\\w-]+)", RegexOption.IGNORE_CASE)

// Lista de correos de los administradores de la iglesia
internal val ADMIN_EMAILS = listOf("malcala45@gmail.com", "pastor@vidaensupalabra.com")

internal external interface FirebaseUser {
    val uid: String
    val name: String
    val photo: String?
    val email: String?
}

// Interoperabilidad con JS para acceso al portapapeles
internal fun copyToClipboard(text: String) = js("window.navigator.clipboard.writeText(text);")

internal fun isAdmin(user: FirebaseUser?): Boolean = user?.email != null && user.email in ADMIN_EMAILS

internal fun parsearMarkdownBasico(texto: String): String {
    var html = texto.replace("<", "&lt;").replace(">", "&gt;")
    html = html.replace(IMG_REGEX, "<img src=\"$2\" alt=\"$1\" style=\"max-width: 100%; max-height: 400px; object-fit: contain; border-radius: 8px; margin: 8px 0; cursor: zoom-in;\"/>")
    html = html.replace(BOLD_REGEX, "<b>$1</b>")
    html = html.replace(ITALIC_REGEX, "<i>$1</i>")
    html = html.replace(LINK_REGEX) { match ->
        val text = match.groupValues[1]
        // Seguridad: Prevenir ataques XSS mediante enlaces javascript:
        val url = match.groupValues[2].let { if (it.trim().startsWith("javascript:", ignoreCase = true)) "#" else it }
        "<a href=\"$url\" target=\"_blank\" style=\"color: #D3A550;\">$text</a>"
    }
    // Auto-vincular URLs e incrustar reproductores nativos directamente en el texto
    html = html.replace(AUTO_LINK_REGEX) { match ->
        val prefix = match.groupValues[1]
        val url = match.groupValues[2]
        when (identificarTipoMultimedia(url)) {
            TipoMultimedia.YOUTUBE -> "$prefix<div style=\"width: 100%; aspect-ratio: 16/9; border-radius: 12px; overflow: hidden; margin: 16px 0;\"><iframe src=\"https://www.youtube.com/embed/${extraerMediaUrl(url)}\" style=\"width: 100%; height: 100%; border: 0; background-color: black;\" allow=\"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture\" allowfullscreen></iframe></div>"
            TipoMultimedia.NATIVO_MP4 -> "$prefix<video controls playsinline style=\"width: 100%; max-height: 400px; object-fit: contain; border-radius: 12px; margin: 16px 0; background-color: black;\"><source src=\"$url\" type=\"video/mp4\"></video>"
            TipoMultimedia.INSTAGRAM -> {
                val igId = extraerIdInstagram(url)
                if (igId != null) "$prefix<iframe src=\"https://www.instagram.com/p/$igId/embed/\" scrolling=\"no\" allowtransparency=\"true\" allow=\"encrypted-media; picture-in-picture\" style=\"width: 100%; max-width: 400px; height: 600px; border: 0; border-radius: 12px; margin: 16px auto; display: block;\"></iframe>"
                else "$prefix<a href=\"$url\" target=\"_blank\" style=\"color: #D3A550;\">$url</a>"
            }
            TipoMultimedia.DESCONOCIDO -> "$prefix<a href=\"$url\" target=\"_blank\" style=\"color: #D3A550;\">$url</a>"
        }
    }
    html = html.replace(QUOTE_REGEX, "<blockquote style=\"border-left: 4px solid #D3A550; margin: 0 0 0 16px; padding-left: 8px; color: #aaa;\">$1</blockquote>")
    html = html.replace("\n", "<br/>")
    return html
}

internal fun aplicarFormatoVisual(formato: String, arg: String = "") {
    val element = window.document.getElementById("post-content-area")
    element?.asDynamic()?.focus()
    window.document.execCommand(formato, false, arg)
}

internal fun htmlToMarkdown(html: String): String {
    var text = html
    val opts = setOf(RegexOption.IGNORE_CASE)
    text = text.replace(Regex("<img\\s+(?:[^>]*?\\s+)?src=([\"'])([\\s\\S]*?)\\1[^>]*>", opts), "![]($2)")
    text = text.replace(Regex("<b[^>]*>([\\s\\S]*?)</b>", opts), "**$1**")
    text = text.replace(Regex("<strong[^>]*>([\\s\\S]*?)</strong>", opts), "**$1**")
    text = text.replace(Regex("<i[^>]*>([\\s\\S]*?)</i>", opts), "*$1*")
    text = text.replace(Regex("<em[^>]*>([\\s\\S]*?)</em>", opts), "*$1*")
    text = text.replace(Regex("<blockquote[^>]*>([\\s\\S]*?)</blockquote>", opts), "> $1\n")
    text = text.replace(Regex("<a\\s+(?:[^>]*?\\s+)?href=([\"'])([\\s\\S]*?)\\1[^>]*>([\\s\\S]*?)</a>", opts), "$3")
    text = text.replace(Regex("<br\\s*/?>", opts), "\n")
    text = text.replace(Regex("</div>", opts), "\n")
    text = text.replace(Regex("</p>", opts), "\n\n")
    text = text.replace(Regex("<[^>]+>"), "")
    text = text.replace("&nbsp;", " ")
    text = text.replace("&lt;", "<")
    text = text.replace("&gt;", ">")
    text = text.replace("&amp;", "&")
    text = text.replace(Regex("\n{3,}"), "\n\n")
    return text.trim()
}

internal fun markdownToWhatsApp(markdown: String): String {
    var text = markdown
    text = text.replace(IMG_REGEX, "🖼️ [Imagen adjunta]")
    text = text.replace(Regex("\\*\\*([\\s\\S]*?)\\*\\*"), "B_O_L_D$1B_O_L_D")
    text = text.replace(Regex("\\*([\\s\\S]*?)\\*"), "_$1_")
    text = text.replace("B_O_L_D", "*")
    text = text.replace(Regex("\\[([\\s\\S]*?)\\]\\(([\\s\\S]*?)\\)")) { match ->
        if (match.groupValues[1] == match.groupValues[2]) match.groupValues[2] else "${match.groupValues[1]}: ${match.groupValues[2]}"
    }
    text = text.replace(Regex("^&gt; (.*?)$", RegexOption.MULTILINE), "\"$1\"")
    text = text.replace(Regex("^> (.*?)$", RegexOption.MULTILINE), "\"$1\"")
    return text.trim()
}

// Evaluador genérico de multimedia para el renderizado condicional en la UI
internal enum class TipoMultimedia { YOUTUBE, NATIVO_MP4, INSTAGRAM, DESCONOCIDO }

internal fun identificarTipoMultimedia(url: String): TipoMultimedia {
    if (url.isBlank()) return TipoMultimedia.DESCONOCIDO
    if (YOUTUBE_REGEX.containsMatchIn(url)) return TipoMultimedia.YOUTUBE
    if (NATIVE_VIDEO_REGEX.containsMatchIn(url)) return TipoMultimedia.NATIVO_MP4
    if (INSTAGRAM_REGEX.containsMatchIn(url)) return TipoMultimedia.INSTAGRAM
    return TipoMultimedia.DESCONOCIDO
}

internal fun extraerMediaUrl(url: String): String {
    if (url.isBlank()) return ""
    val ytId = YOUTUBE_REGEX.find(url)?.groupValues?.get(1)
    if (ytId != null) return ytId
    return url // Permite guardar cualquier enlace (MP4, Instagram, etc.) para que el factory decida
}

internal fun extraerIdInstagram(url: String): String? {
    return INSTAGRAM_REGEX.find(url)?.groupValues?.get(3)
}

internal fun getCurrentFormattedDate(): String {
    val localTime = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    val meses = listOf("Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic")
    return "${localTime.dayOfMonth} ${meses[localTime.monthNumber - 1]} ${localTime.year}"
}

internal suspend fun compartirPublicacionNativa(post: ComunidadPost) {
    val formattedContent = markdownToWhatsApp(post.contenido)
    var textToShare = "${post.titulo}\nPor ${post.autor}\n\n$formattedContent"
    
    if (post.youtubeVideoId != null) {
        if (identificarTipoMultimedia(post.youtubeVideoId) == TipoMultimedia.YOUTUBE) {
            textToShare += "\n\nMira el video aquí: https://youtu.be/${post.youtubeVideoId}"
        } else {
            textToShare += "\n\nMira el video aquí: ${post.youtubeVideoId}" // Video nativo MP4
        }
    }
    textToShare += "\n\nEncuentra más en la App Vida en Su Palabra: https://vidaensupalabra.pages.dev/?postId=${post.id}"

    var shared = false
    val nav = window.navigator.asDynamic()

        // Encontrar si existe alguna imagen incrustada en el WYSIWYG
        val inlineImageMatch = IMG_REGEX.find(post.contenido)
        val firstInlineImageUrl = inlineImageMatch?.groupValues?.get(2)
        val imageToShare = post.imagenUrl ?: firstInlineImageUrl

        if (imageToShare != null && nav.canShare != undefined) {
        try {
                val response = window.fetch(imageToShare).await()
            val blob = response.blob().await()
            val file = js("new File([blob], 'imagen.jpg', {type: blob.type})")
            
            val shareData = js("({})")
            shareData.text = textToShare
            shareData.files = arrayOf(file)
            
            if (nav.canShare(shareData) as Boolean) {
                val sharePromise = nav.share(shareData) as kotlin.js.Promise<dynamic>
                sharePromise.await()
                shared = true
            }
        } catch (e: Exception) {
            console.log("Error al compartir imagen como archivo:", e)
        }
    }

    if (!shared) {
            if (imageToShare != null) {
                textToShare = textToShare.replace("\n\nEncuentra más", "\n\nMira la imagen aquí: ${imageToShare}\n\nEncuentra más")
        }
        if (!shareText(textToShare)) {
            copyToClipboard(textToShare)
            window.alert("¡Publicación copiada al portapapeles!")
        }
    }
}